/**
 * DbStorage — where the SQLite file lives between sessions.
 *
 * A port, so the database layer never learns whether it is running in a browser
 * with OPFS or in a Node test with nothing at all.
 */

import { forDom } from './bytes';

export interface DbStorage {
  load(): Promise<Uint8Array | null>;
  save(bytes: Uint8Array): Promise<void>;
}

/** Keeps the database in memory only. Used by tests and by private-mode fallback. */
export class MemoryDbStorage implements DbStorage {
  private bytes: Uint8Array | null = null;

  async load(): Promise<Uint8Array | null> {
    return this.bytes;
  }

  async save(bytes: Uint8Array): Promise<void> {
    this.bytes = bytes;
  }
}

/** Stores the database as a single file in the Origin Private File System. */
export class OpfsDbStorage implements DbStorage {
  constructor(private readonly filename = 'm3cull.sqlite') {}

  async load(): Promise<Uint8Array | null> {
    try {
      const dir = await navigator.storage.getDirectory();
      const handle = await dir.getFileHandle(this.filename);
      const file = await handle.getFile();
      if (file.size === 0) return null;
      return new Uint8Array(await file.arrayBuffer());
    } catch {
      return null; // absent or unreadable — caller creates a fresh database
    }
  }

  async save(bytes: Uint8Array): Promise<void> {
    const dir = await navigator.storage.getDirectory();
    // Write to a temp name first so a crash mid-write cannot leave a torn file.
    const tmpName = `${this.filename}.tmp`;
    const tmp = await dir.getFileHandle(tmpName, { create: true });

    const writable = await tmp.createWritable();
    try {
      await writable.write(forDom(bytes));
    } finally {
      await writable.close();
    }

    await dir.removeEntry(this.filename).catch(() => undefined);
    // OPFS has no rename, so copy the committed bytes across and drop the temp.
    const final = await dir.getFileHandle(this.filename, { create: true });
    const w2 = await final.createWritable();
    try {
      await w2.write(forDom(bytes));
    } finally {
      await w2.close();
    }
    await dir.removeEntry(tmpName).catch(() => undefined);
  }
}
